import React from 'react'
import {
  Button,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  Switch,
  Typography
} from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { servicesApi } from 'api/services'
import { landingPagesApi } from 'api/landingPages'
import { IServiceCategory, IServiceSlotDay } from 'models/services'
import { Uploader } from 'components/ui/Uploader/Uploader'
import { superAdminRoutes } from 'shared/constants/routes'
import { IMAGES_URL } from 'shared/constants/urls'
import { showServerError } from 'shared/utils/showServerError'
import { showMessage } from 'shared/utils/showMessage'
import { POLO_CLUB_SLUG } from './PoloClub'
import { SlotScheduleEditor } from './SlotScheduleEditor'
import { StaffEmailsInput } from './StaffEmailsInput'

// адрес и координаты по умолчанию для услуг ПолоКлуба (подставляются в поля, можно менять)
const DEFAULT_ADDRESS =
  'Московская область, город Химки, микрорайон Сходня, улица Некрасова, 2М'
const DEFAULT_COORDINATES = '55.941460, 37.272310'
const DEFAULT_LAT = 55.94146
const DEFAULT_LNG = 37.27231
const DEFAULT_PHONE = '+7 (495) 085-10-30'
const DEFAULT_CONTACT_NAME = 'Дмитрий'
const DEFAULT_STAFF_EMAIL = 'grigoryevkd@plitprom.ru'

// одно поле координат в формате "широта, долгота" → { lat, lng }; null если формат неверный
const parseCoordinates = (
  value?: string
): { lat: number; lng: number } | null => {
  if (!value) return null
  const parts = value.split(',').map((p) => p.trim())
  if (parts.length !== 2) return null
  const lat = Number(parts[0])
  const lng = Number(parts[1])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

const formatCoordinates = (lat: number, lng: number) => `${lat}, ${lng}`

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// пустые поля — это хвост списка, а не ошибка: их отбрасываем при отправке
const cleanEmails = (emails?: string[]) =>
  (emails ?? []).map((email) => email.trim()).filter(Boolean)

export const CreateService: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = Boolean(id)
  const [form] = Form.useForm()
  const durationMin = Form.useWatch('duration', form)

  const [categories, setCategories] = React.useState<IServiceCategory[]>([])
  const [pageId, setPageId] = React.useState<string | null>(null)
  const [slots, setSlots] = React.useState<IServiceSlotDay[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  React.useEffect(() => {
    const load = async () => {
      try {
        const [categoriesRes, pageRes] = await Promise.all([
          // категории подпроекта: услуги ПолоКлуба привязываются только к его категориям
          servicesApi.getServiceCategories(POLO_CLUB_SLUG),
          landingPagesApi.getBySlug(POLO_CLUB_SLUG)
        ])
        setCategories(categoriesRes.data)
        setPageId(pageRes.data._id)
        // пул почт страницы: им заполняется форма новой услуги
        const pooledEmails = pageRes.data.staff_emails?.length
          ? pageRes.data.staff_emails
          : [DEFAULT_STAFF_EMAIL]

        if (isEdit && id) {
          const { data: service } = await servicesApi.getServiceById(id)
          const categoryId =
            typeof service.category === 'object'
              ? service.category._id
              : service.category
          // услуги, заведённые до нескольких адресов, отдают одиночный staff_email
          const legacyEmails = service.staff_email
            ? [service.staff_email]
            : pooledEmails
          form.setFieldsValue({
            name: service.name,
            description: service.description,
            category: categoryId,
            price: service.price,
            duration: service.duration,
            capacity: service.capacity ?? 1,
            prepayment_required: service.prepayment_required !== false,
            staff_emails: service.staff_emails?.length
              ? service.staff_emails
              : legacyEmails,
            address: service.location?.name,
            phone: service.phone ?? DEFAULT_PHONE,
            contact_name: service.contact_name ?? DEFAULT_CONTACT_NAME,
            coordinates: formatCoordinates(
              service.location?.lat ?? DEFAULT_LAT,
              service.location?.lng ?? DEFAULT_LNG
            ),
            // старые услуги без галереи — показываем одиночное photo как первый элемент
            photos: (service.photos?.length ? service.photos : service.photo ? [service.photo] : []).map(
              (p, i) => ({
                uid: `-${i + 1}`,
                name: p,
                status: 'done',
                url: `${IMAGES_URL}${p}`
              })
            )
          })
          setSlots(service.slots ?? [])
        } else {
          form.setFieldsValue({
            address: DEFAULT_ADDRESS,
            phone: DEFAULT_PHONE,
            contact_name: DEFAULT_CONTACT_NAME,
            coordinates: DEFAULT_COORDINATES,
            capacity: 1,
            prepayment_required: true,
            staff_emails: pooledEmails
          })
        }
      } catch (e) {
        showServerError(e)
      } finally {
        setIsLoading(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleSubmit = async (values: any) => {
    try {
      setIsSubmitting(true)
      // валидатор поля гарантирует корректный формат; на всякий случай откат к дефолту
      const coords = parseCoordinates(values.coordinates) ?? {
        lat: DEFAULT_LAT,
        lng: DEFAULT_LNG
      }
      // делим галерею: новые файлы → photos, оставленные (по url) → existing_photos (имя файла)
      const fileList: any[] = values.photos ?? []
      const photos = fileList
        .map((f) => f.originFileObj)
        .filter(Boolean) as File[]
      const existing_photos = fileList
        .filter((f) => !f.originFileObj && f.url)
        .map((f) => (f.url as string).replace(IMAGES_URL, ''))
      const payload = {
        name: values.name,
        description: values.description,
        category: values.category,
        price: values.price,
        duration: values.duration,
        capacity: values.capacity,
        prepayment_required: values.prepayment_required,
        staff_emails: cleanEmails(values.staff_emails),
        slots,
        phone: values.phone,
        contact_name: values.contact_name,
        location: {
          name: values.address,
          lat: coords.lat,
          lng: coords.lng
        }
      }

      if (isEdit && id) {
        await servicesApi.updatePageService(id, {
          ...payload,
          ...(photos.length && { photos }),
          existing_photos
        })
        showMessage('success', 'Услуга обновлена', '')
      } else {
        if (!pageId) return
        await servicesApi.createPageService({ ...payload, page_id: pageId, photos })
        showMessage('success', 'Услуга создана', '')
      }
      navigate(superAdminRoutes.POLO_CLUB)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
        <Spin size='large' />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 720, paddingBottom: 80 }}>
      <Button
        type='link'
        icon={<ArrowLeftOutlined />}
        style={{ paddingLeft: 0, marginBottom: 8 }}
        onClick={() => navigate(superAdminRoutes.POLO_CLUB)}
      >
        Назад к ПолоКлубу
      </Button>
      <Typography.Title level={4}>
        {isEdit ? 'Редактировать услугу' : 'Добавить услугу'}
      </Typography.Title>

      <Form form={form} layout='vertical' onFinish={handleSubmit}>
        <Uploader
          name='photos'
          rules={[{ required: true, message: 'Добавьте изображение' }]}
          type='variation'
          multiple
          maxCount={10}
          hint='Фото услуги (можно несколько; первое — обложка)'
        />

        <Form.Item
          label='Название'
          name='name'
          rules={[{ required: true, message: 'Укажите название' }]}
        >
          <Input placeholder='Название услуги' />
        </Form.Item>

        <Form.Item label='Описание' name='description'>
          <Input.TextArea rows={4} placeholder='Описание услуги' />
        </Form.Item>

        <Form.Item
          label='Категория'
          name='category'
          rules={[{ required: true, message: 'Выберите категорию' }]}
        >
          <Select
            placeholder='Категория'
            options={categories.map((c) => ({ value: c._id, label: c.name }))}
          />
        </Form.Item>

        <div style={{ display: 'flex', gap: 16 }}>
          <Form.Item
            label='Цена, ₽'
            name='price'
            rules={[{ required: true, message: 'Укажите цену' }]}
            style={{ flex: 1 }}
          >
            <InputNumber min={0} precision={2} style={{ width: '100%' }} placeholder='0' />
          </Form.Item>
          <Form.Item
            label='Длительность занятия, мин'
            name='duration'
            style={{ flex: 1 }}
          >
            <InputNumber min={0} step={15} style={{ width: '100%' }} placeholder='120' />
          </Form.Item>
          <Form.Item
            label='Мест на слот'
            name='capacity'
            style={{ flex: 1 }}
            extra='Сколько человек можно записать на одно время'
          >
            <InputNumber min={1} step={1} style={{ width: '100%' }} placeholder='1' />
          </Form.Item>
        </div>

        <Form.Item
          label='Требуется оплата заранее'
          name='prepayment_required'
          valuePropName='checked'
          extra='Если выключено — на услугу можно записаться без онлайн-оплаты'
        >
          <Switch />
        </Form.Item>

        <Form.Item
          label='Почта сотрудника'
          name='staff_emails'
          rules={[
            {
              validator: (_, value?: string[]) => {
                const invalid = cleanEmails(value).find(
                  (email) => !EMAIL_PATTERN.test(email)
                )
                return invalid
                  ? Promise.reject(new Error(`Некорректный адрес: ${invalid}`))
                  : Promise.resolve()
              }
            }
          ]}
          extra='Дублируем сюда уведомления о записях на эту услугу. Адреса запоминаются и подставляются в следующую услугу'
        >
          <StaffEmailsInput placeholder={DEFAULT_STAFF_EMAIL} />
        </Form.Item>

        <Form.Item label='Слоты для записи'>
          <SlotScheduleEditor
            value={slots}
            durationMin={durationMin}
            onChange={setSlots}
          />
        </Form.Item>

        <Form.Item
          label='Телефон'
          name='phone'
          rules={[{ required: true, message: 'Укажите телефон' }]}
        >
          <Input placeholder='+7 (495) 085-10-30' />
        </Form.Item>

        <Form.Item
          label='Контактное лицо'
          name='contact_name'
          rules={[{ required: true, message: 'Укажите контактное лицо' }]}
          extra='Показывается рядом с телефоном на сайте и как владелец услуги в приложении'
        >
          <Input placeholder={DEFAULT_CONTACT_NAME} />
        </Form.Item>

        <Form.Item
          label='Адрес'
          name='address'
          rules={[{ required: true, message: 'Укажите адрес' }]}
        >
          <Input placeholder='Адрес оказания услуги' />
        </Form.Item>

        <Form.Item
          label='Координаты (широта, долгота)'
          name='coordinates'
          rules={[
            { required: true, message: 'Укажите координаты' },
            {
              validator: (_, value) =>
                parseCoordinates(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error('Формат: 55.941460, 37.272310'))
            }
          ]}
          extra='Широта и долгота через запятую, например 55.941460, 37.272310'
        >
          <Input placeholder='55.941460, 37.272310' />
        </Form.Item>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={() => navigate(superAdminRoutes.POLO_CLUB)}>
            Отмена
          </Button>
          <Button type='primary' htmlType='submit' loading={isSubmitting}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </div>
      </Form>
    </div>
  )
}
